import { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Paper, Typography, TextField, Button, IconButton, useMediaQuery, useTheme, Grid, Divider, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGrid } from '@mui/x-data-grid';
import DropdownProductCode from './DropdownProductCode';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import EditIcon from '@mui/icons-material/Edit';
import Dropdown from '../../../components/Dropdown';
import { useVisibleColumns } from '../../../hooks/useVisibleColumns';
import { useUserDefine } from '../../../context/UserDefineContext';
import { COL, DEFAULT_LABELS } from '../../../constants/columnConstants';
import { useStockValidation } from '../../../hooks/useStockValidation';

// "Apply in Amt = Pkg*Qty*Rate?" — when this configure is enabled, a Pkg field
// containing anything that isn't a clean number (letters/special characters) must
// make the Pkg*Qty*Rate calculation come out as 0, instead of calculateAmount()'s
// own internal `Number(pkg) || 1` silently promoting invalid input back to 1.
// Blank/null/undefined Pkg is untouched — it still defaults to 1 (existing
// behavior for a fresh row before the user has typed a Pkg value).
const isGarbagePkg = (rawPkg) =>
    rawPkg !== '' && rawPkg !== null && rawPkg !== undefined && Number.isNaN(Number(rawPkg));

const getAmountCalcSafe = (qty, rate, rawPkg, disc, isApplyPkgInAmtEnabled, calculateAmount) => {
    if (isApplyPkgInAmtEnabled && isGarbagePkg(rawPkg)) {
        return { gross: 0, taxable: 0 };
    }
    return calculateAmount(qty, rate, rawPkg, disc);
};

const ProductDetails = ({ initialData, L_No, onNext, clientData, challanIds, connectionString, mode, onRowsChange }) => {
    console.log('📝 DebitNote Component initialized with L_No:', L_No);
    const { user, selectedCompany, loading, configureData } = useAuth();
    console.log('📋 DebitNote ProductDetails: configureData from useAuth:', configureData?.length || 0, 'items');
    const { validateMinLevel, isLastSaleRateEnabled, getLastSaleRate, isApplyMRPInPurchaseEnabled, calculatePurchaseRateFromMRP, isApplyPkgInAmtEnabled, calculateAmount, isApplyRateOnConversionEnabled, calculateConversion , isDiscSchemeEnabled, getClientDiscScheme } = useStockValidation(connectionString, selectedCompany?.CompId);
    console.log('🔧 DebitNote: validateMinLevel hook initialized, selectedCompany:', selectedCompany?.CompId);
    console.log('💾 DebitNote: isLastSaleRateEnabled:', isLastSaleRateEnabled);
    const { isColVisible, getColLabel, getColRow } = useVisibleColumns('DEBIT');
    // RupessToBeAsk=1 ("Rupees") -> only the Oth field itself is shown (as before).
    // RupessToBeAsk=0 ("Percentage") -> also show its disabled, read-only Amount field.
    const isOthRupeesMode = (colNo) => Boolean(getColRow(colNo)?.RupessToBeAsk);
    // Oth1→Oth7: each one's amount is applied in order on top of the running
    // TaxableAmt (qty*rate*(1-disc/100) as the starting base) — Percentage mode
    // takes its % against the running total so far (not the original base),
    // Rupees mode adds/subtracts a flat value; AddLess=1 means subtract, 0 means add.
    const OTH_FIELDS = [
        { field: 'oth1', col: COL.OTH1 },
        { field: 'oth2', col: COL.OTH2 },
        { field: 'oth3', col: COL.OTH3 },
        { field: 'oth4', col: COL.OTH4 },
        { field: 'oth5', col: COL.OTH5 },
        { field: 'oth6', col: COL.OTH6 },
        { field: 'oth7', col: COL.OTH7 },
    ];
    // Rupees mode: OthN itself stays 0 in the DB (decimal(5,2), not meant to
    // hold a rupee amount) — the real typed value lives in OthNAmt directly.
    // Percentage mode: the user types the % into OthN, OthNAmt is computed.
    const othDisplayField = (field, col) => isOthRupeesMode(col) ? `${field}Amt` : field;
    // Applies Oth1→Oth7 in order on top of `base`, returning both the final
    // taxable amount AND each Oth's own computed rupee contribution (so the
    // disabled "Oth N Amount" field — oth{N}Amt — has something to show in
    // Percentage mode). Used both for the live preview while editing and for
    // the actual Add/Update Product commit, so the two never disagree.
    const applyOthCharges = (base, rowState) => {
        let running = base;
        const amounts = {};
        OTH_FIELDS.forEach(({ field, col }) => {
            const amtField = `${field}Amt`;
            const row = getColRow(col);
            const isRupees = Boolean(row?.RupessToBeAsk);
            const isMinus = Boolean(row?.AddLess);
            const raw = isRupees ? rowState[amtField] : rowState[field];
            const val = Number(raw);
            if (!raw || Number.isNaN(val) || val === 0) {
                amounts[amtField] = null;
                return;
            }
            const contribution = isRupees ? val : (running * val) / 100;
            // Rupees mode: amtField IS the field the user is actively typing into
            // (othDisplayField routes it there) — reformatting it with toFixed(2)
            // on every keystroke changes the string under the cursor and resets
            // cursor position to the end. Keep the raw typed text as-is; it's only
            // ever Number()'d downstream, so the unformatted string is fine.
            // Percentage mode's amtField is a separate disabled/read-only display,
            // so reformatting it is safe and keeps it looking like a computed amount.
            amounts[amtField] = isRupees ? raw : contribution.toFixed(2);
            running = isMinus ? running - contribution : running + contribution;
        });
        return { taxableAmt: running, amounts };
    };
    // Recomputes base=qty*rate*(1-disc/100) + Oth1→Oth7 from whatever's currently
    // in `updated`, returning the fields to merge back into state. Shared by both
    // the Oth field handler and the Qty/Rate/Disc handlers so TaxableAmt/amount
    // stay Oth-adjusted live, no matter which of the four fields just changed.
    const recomputeTaxable = (updated) => {
        const qty = Number(updated.qty) || 0;
        const rate = Number(updated.rate) || 0;
        const disc = Number(updated.disc) || 0;
        // Use the same Pkg-aware calc as Add Product/handleNext (getAmountCalcSafe)
        // instead of raw qty*rate*(1-disc/100) — otherwise live Oth1-7 typing
        // ignores Pkg*Qty*Rate when "Apply in Amt = Pkg*Qty*Rate?" is enabled.
        const base = getAmountCalcSafe(qty, rate, updated.pkg, disc, isApplyPkgInAmtEnabled, calculateAmount).taxable;
        const { taxableAmt, amounts } = applyOthCharges(base, updated);
        return { ...amounts, TaxableAmt: taxableAmt.toFixed(2), amount: taxableAmt.toFixed(2) };
    };
    const handleOthChange = (field, col) => (e) => {
        let raw = e.target.value;
        // Numbers only (digits with at most one decimal point) — no letters/special chars
        if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
        if (!isOthRupeesMode(col) && raw !== '') {
            // Percentage mode: clamp to [0, 100]
            const num = Number(raw);
            if (num > 100) raw = '100';
            if (num < 0) raw = '0';
        }
        const targetKey = othDisplayField(field, col);
        setCurrent(prev => {
            const updated = { ...prev, [targetKey]: raw };
            return { ...updated, ...recomputeTaxable(updated) };
        });
    };
    const { getSelectedCharges, getChargeItemId, userDefineData } = useUserDefine();
    // getSelectedCharges() returns a brand-new array on every call (Array.filter),
    // even when userDefineData hasn't changed — used as a useEffect dependency
    // further down, an unstable reference there re-fires those effects (and their
    // setState calls) on every render, causing an infinite render loop. Memoize
    // on userDefineData (the actual stable source data) so the array reference
    // only changes when the underlying charges data actually changes.
    const additionalCharges = useMemo(() => getSelectedCharges('DEBIT'), [userDefineData]);
    const [chargesDialogOpen, setChargesDialogOpen] = useState(false);
    const [chargesCollapsibleOpen, setChargesCollapsibleOpen] = useState(true);
    const [chargeInputs, setChargeInputs] = useState({});
    // Additional Charges contribution applied to the summary panel — populated
    // only when the user clicks Save in the Charges popup (see handleSaveCharges).
    const [chargesSummary, setChargesSummary] = useState({ total: 0, cgst: 0, sgst: 0, igst: 0 });
    const [gsts, setGsts] = useState([]);

    // Restore chargeInputs and chargesSummary from initialData if provided (when loading an existing quotation)
    useEffect(() => {
        // API chargeInputs are already keyed by TransType (200, 201, 202, 203, 204)
        // which matches charge.TransType in additionalCharges, so no mapping needed
        if (initialData?.chargeInputs && Object.keys(initialData.chargeInputs).length > 0) {
            console.log('[ChargeInputs Debug] API chargeInputs keys:', Object.keys(initialData.chargeInputs));
            if (additionalCharges && additionalCharges.length > 0) {
                console.log('[ChargeInputs Debug] additionalCharges:', additionalCharges.map(c => ({ TransType: c.TransType, SrNo: c.SrNo, name: c.ChargeName })));
            }
            setChargeInputs(initialData.chargeInputs);
        }

        // Restore chargesSummary from charge rows in tableData (has actual calculated tax amounts)
        // This is more reliable since the API calculates taxes based on actual requirements
        if (initialData?.tableData) {
            const chargesTaxData = { total: 0, cgst: 0, sgst: 0, igst: 0 };

            initialData.tableData.forEach(row => {
                const transType = row.TransType != null ? parseInt(row.TransType) : 100;
                const isCharge = transType >= 200 && transType <= 204;
                if (isCharge) {
                    // Use the amount from the charge row
                    chargesTaxData.total += Number(row.amount || 0);
                    // Add the tax amounts from the charge row (already calculated by backend)
                    chargesTaxData.cgst += Number(row.CGSTAmt || 0);
                    chargesTaxData.sgst += Number(row.SGSTAmt || 0);
                    chargesTaxData.igst += Number(row.IGSTAmt || 0);
                }
            });

            console.log('[ChargesSummary Debug] Calculated from tableData:', chargesTaxData);

            if (chargesTaxData.total > 0 || chargesTaxData.cgst > 0 || chargesTaxData.sgst > 0 || chargesTaxData.igst > 0) {
                setChargesSummary(chargesTaxData);
            }
        }
        // Depend on the specific fields read above, not the whole initialData object —
        // initialData is a fresh object literal from the parent on every render
        // (formData.product || {}), so depending on it directly re-fires this effect
        // (and its setChargesSummary call) on every parent re-render, not just when
        // the actual saved charge data changes.
    }, [initialData?.chargeInputs, initialData?.tableData]);

    // Populate each charge slot's editable state (Amount, and Tax when TaxEnabled)
    // fresh every time the popup opens — default Tax is whatever was saved for
    // that slot in UserDefineColumns (charge.TaxId). Preserve amount if already restored from initialData.
    useEffect(() => {
        if (!chargesDialogOpen) return;
        setChargeInputs(prev => {
            const next = { ...prev };
            additionalCharges.forEach(charge => {
                const key = charge.TransType;
                if (!next[key]) {
                    // Only set empty amount if not already populated (e.g., from initialData restore)
                    next[key] = {
                        amount: '',
                        taxId: charge.TaxId ?? null,
                    };
                } else {
                    // Preserve existing amount, but update taxId to the configured one if not already set
                    if (next[key].taxId === null && charge.TaxId !== null) {
                        next[key].taxId = charge.TaxId;
                    } else if (charge.TaxEnabled && next[key].taxId != null && gsts.length) {
                        // Ref mode (loading from an existing Challan/PO) can restore a
                        // taxId that was saved back when this charge slot was NOT
                        // tax-enabled — resolving to 0%/Tax-Free even though the charge
                        // is now configured to carry tax. Swap in the charge's current
                        // default TaxId so the popup reflects today's configuration
                        // instead of a stale 0% rate baked into the source record.
                        const restoredGST = gsts.find(g => String(g.Id) === String(next[key].taxId));
                        const restoredPercent = restoredGST ? getTaxPercent(restoredGST.value) : 0;
                        if (restoredPercent === 0 && charge.TaxId != null) {
                            next[key].taxId = charge.TaxId;
                        }
                    }
                }
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chargesDialogOpen, additionalCharges, gsts]);

    const updateChargeInput = (key, patch) => {
        setChargeInputs(prev => ({
            ...prev,
            [key]: { ...prev[key], ...patch },
        }));
    };
    const companyId = selectedCompany?.CompId || null;
    console.log("ididi", initialData)
    const state = clientData?.details?.['State'] || '';
    const compState = selectedCompany?.State || '';
    // When this Debit Note was raised against a Purchase Invoice (PIID present),
    // the product list must mirror that invoice exactly — editing is locked.
    const isProductEditLocked = clientData?.PIID !== undefined && clientData?.PIID !== null && String(clientData.PIID).trim() !== '';
    console.log('challanIds:', challanIds);
    console.log(`state: ${state} || compState: ${compState}`);
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [stockWarningOpen, setStockWarningOpen] = useState(false);
    const [stockWarningData, setStockWarningData] = useState(null);
    const [stockConfirmResolve, setStockConfirmResolve] = useState(null);

    // ✅ Auto-sync chargesSummary with chargeInputs whenever charges are entered
    // This ensures charges are calculated in real-time without needing a Save button
    useEffect(() => {
        const isSameState = state?.toLowerCase() === compState?.toLowerCase();
        let addTotal = 0, addCgst = 0, addSgst = 0, addIgst = 0;
        additionalCharges.forEach(charge => {
            const key = charge.TransType;
            const input = chargeInputs[key];
            if (!input || !input.amount) return;
            const amount = Number(input.amount) || 0;
            const { taxAmount } = getChargeFinalAmounts(charge, input);
            addTotal += amount;
            if (charge.TaxEnabled) {
                if (isSameState) {
                    addCgst += taxAmount / 2;
                    addSgst += taxAmount / 2;
                } else {
                    addIgst += taxAmount;
                }
            }
        });
        setChargesSummary({ total: addTotal, cgst: addCgst, sgst: addSgst, igst: addIgst });
    }, [chargeInputs, additionalCharges, state, compState, gsts]);

    const [rows, setRows] = useState([]);
    const [nextSrNo, setNextSrNo] = useState(1);
    const [current, setCurrent] = useState({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, MRP: '', batchNo: '', expDate: '', serialNo: '', amount: '', taxAmount: '', remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', GSTCess: '0.00', CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', TaxId: null, challanDtsID: null, challanID: null, soId: null, soDtsID: null, refNo: '', refDate: '', refQty: '', type: '', size: '', color: '', freeQty: '', per: '', pkg: '', oth1: '', oth2: '', oth3: '', GSTOth: '0.00', poQty: '', ex4: '', ex5: '', exQty6: '', serialNo: '', oth4: '', oth5: '', oth6: '', oth7: '', TransType: 100,
        // "Enable Convesrion effect in Transaction?" fields — defaultUnitId/convUnitId/productConvQty
        // describe the PRODUCT (its own Unit, and its registered ConvUnit/ConvQty) and
        // never change while this row's product stays the same. baseRate preserves the
        // rate as it stood before any conversion division, so switching Unit back to
        // defaultUnitId can restore it exactly. ConversionQty/ConversionRate are only
        // non-null while a conversion is actively applied to this row (see
        // saleinvoicedetails.ConversionQty/ConversionRate columns).
        defaultUnitId: '', convUnitId: '', productConvQty: '', baseRate: 0, ConversionQty: null, ConversionRate: null });
    const [totals, setTotals] = useState({ basic: 0, grossBasic: 0, discount: 0, cgst: 0, sgst: 0, igst: 0, roundOff: 0, total: 0 });
    const [errors, setErrors] = useState({});
    const [editIndex, setEditIndex] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [unitOptions, setUnitOptions] = useState([]);
    const [colorMap, setColorMap] = useState({});
    const [sizeMap, setSizeMap] = useState({});
    const [originalEditQty, setOriginalEditQty] = useState(null);
    const [isEditingMode, setIsEditingMode] = useState(false);


    const [isInitialized, setIsInitialized] = useState(false);

    // "Enable Convesrion effect in Transaction?" — popup shown whenever the row's Unit is
    // changed away from the product's own default Unit. pendingUnitChange holds
    // the Unit selection waiting on the user's Conversion Qty confirmation, so
    // Cancel can restore the previous Unit untouched.
    const [conversionQtyDialogOpen, setConversionQtyDialogOpen] = useState(false);
    const [conversionQtyInput, setConversionQtyInput] = useState('');
    const [pendingUnitChange, setPendingUnitChange] = useState(null);

    const requiredAsteriskStyle = {
        '& .MuiFormLabel-asterisk': {
            color: 'red',
        },
    };

    const API_BASE = `${connectionString}/api`;

    // Track if component has finished initializing
    const hasInitializedRef = useRef(false);

    // When true, the next run of the live-calculation effect is skipped —
    // set right before a row's DB values are loaded into `current` (editRow),
    // so entering edit mode doesn't immediately overwrite the stored amount.
    const skipNextCalcRef = useRef(false);

    // Ref to the Product Code field for auto-focus
    const productCodeRef = useRef(null);

    // Notify parent when rows change â€” but ONLY after initialization is done
    useEffect(() => {
        if (!hasInitializedRef.current) return; // Don't fire on first empty render
        if (onRowsChange) {
            onRowsChange(rows);
        }
    }, [rows]);

    // Reset isInitialized when coming back with data but rows are empty
    useEffect(() => {
        if (initialData?.tableData?.length && rows.length === 0 && hasInitializedRef.current) {
            setIsInitialized(false);
        }
    }, [initialData?.tableData]);

    // For add mode â€” no initialData, so mark as initialized immediately
    useEffect(() => {
        if (!initialData?.tableData?.length) {
            hasInitializedRef.current = true;
        }
    }, []);

    const transformGSTs = data =>
        Array.isArray(data)
            ? data.map(i => ({ Id: i.TaxID, value: i.Taxcode }))
            : [];

    const Hlp_Fn_GST = async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/product-get-data/api/gst?companyId=${companyId}`);
            console.log(data)
            setGsts(transformGSTs(data));
        } catch (e) {
            console.error('GST lookup failed', e);
            setSnackbar({ open: true, message: 'GST lookup failed.', severity: 'error' });
            setGsts([]);
        }
    };

    useEffect(() => {
        Hlp_Fn_GST();
    }, []);


    // console.log("unitOptions" , unitOptions)


    useEffect(() => {
        const fetchData = async () => {
            try {
                const unitsRes = await axios.get(`${connectionString}/api/unit/retrive`, { params: { companyId } });
                setUnitOptions(unitsRes.data); // Expecting [{ UnitId: 43, unit: "NOS" }, ...]
                const [cRes, sRes] = await Promise.all([
                    axios.get(`${connectionString}/api/colormaster`),
                    axios.get(`${connectionString}/api/sizemaster`)
                ]);
                const cm = {}, sm = {};
                cRes.data.forEach(c => (cm[c.colorId] = { name: c.itemColor, code: c.colorCode }));
                sRes.data.forEach(s => (sm[s.sizeId] = { name: s.itemSize, code: s.sizeCode }));
                setColorMap(cm);
                setSizeMap(sm);
            } catch (error) {
                console.error('Error fetching product or unit data:', error);
            }
        };

        fetchData();
        loadAllProducts();
    }, []);

    const handleProductAdded = async () => {
        // Reload units when product is added
        try {
            const unitsRes = await axios.get(`${connectionString}/api/unit/retrive`, {
                params: { companyId }
            });
            setUnitOptions(unitsRes.data);

            // Also reload products
            await loadAllProducts();
        } catch (error) {
            console.error('Error refreshing data:', error);
        }
    };

    const loadAllProducts = async () => {
        try {
            const res = await axios.get(`${connectionString}/api/productdata/retrive`, { params: { companyId, mode: 'PURC' } });
            setAllProducts(res.data || []);
        } catch (e) {
            console.error('Error fetching products:', e);
        }
    };


    const getTaxPercent = (value) => {
        if (!value) return 0;

        // remove % sign
        const cleaned = value.replace('%', '').trim();

        const parsed = parseFloat(cleaned);

        return isNaN(parsed) ? 0 : parsed;
    };

    useEffect(() => {
        if (isInitialized || !initialData?.tableData || !Array.isArray(initialData.tableData) || !unitOptions.length || !allProducts.length) {
            return;
        }

        // The products grid (`rows`) must only ever hold normal product rows.
        // Additional Charges (TransType 200-204) and RoundOff (300) are restored
        // separately (chargeInputs / roundOff field) and re-added at submit time —
        // including them here as well would duplicate them in the outgoing payload.
        const regularRows = initialData.tableData.filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300);

        const initialRows = regularRows.map((r, index) => {
            const srNoValue = regularRows.length - index;
            const qty = Number(r.dispQty ?? r.qty) || 0;
            const rate = Number(r.rate) || 0;
            const disc = Number(r.disc) || 0;
            // Prefer the exact amount stored in the database over recomputing it —
            // recomputing here can silently diverge from what was actually saved.
            // Only fall back to qty*rate*(1-disc/100) when the DB has no stored amount.
            const storedAmt = r.TaxableAmt ?? r.amount;
            const hasStoredAmt = storedAmt != null && storedAmt !== '' && !Number.isNaN(Number(storedAmt));
            const taxableAmt = hasStoredAmt ? Number(storedAmt) : qty * rate * (1 - disc / 100);

            let taxId = r.taxId || r.TaxId || null;

            //  GST lookup
            const matchedGST = gsts.find(g => String(g.Id) === String(taxId));

            //  final %
            let tax = matchedGST
                ? getTaxPercent(matchedGST.value)
                : Number(r.tax) || (Number(r.CGST) + Number(r.SGST)) || Number(r.IGST) || 0;

            const product = allProducts.find(p => p.Id === r.productId || p.itemCode === r.productCode);

            let taxAmount = taxableAmt * (tax / 100);
            let cgst = '0.00', sgst = '0.00', igst = '0.00';
            let cgstAmt = '0.00', sgstAmt = '0.00', igstAmt = '0.00';

            const matchedUnit =
                unitOptions.find(u => String(u.UnitId) === String(r.UnitId)) ||
                unitOptions.find(u => u.unit?.toLowerCase() === String(r.unit).toLowerCase()) ||
                (product ? unitOptions.find(u =>
                    String(u.UnitId) === String(product.UnitId) ||
                    u.unit?.toLowerCase() === String(product.Unit?.toLowerCase())
                ) : null);

            if (product) {
                tax = tax;       //  computed tax value
                taxId = r.taxId || r.TaxId || null;        //  backend value
                taxAmount = taxableAmt * (tax / 100);
                cgst = state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00';
                sgst = state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00';
                igst = state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00';
                cgstAmt = state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00';
                sgstAmt = state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00';
                igstAmt = state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00';
            } else {
                taxAmount = taxableAmt * (tax / 100);
                cgst = Number(r.CGST || (state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2) : 0)).toFixed(2);
                sgst = Number(r.SGST || (state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2) : 0)).toFixed(2);
                igst = Number(r.IGST || (state?.toLowerCase() !== compState?.toLowerCase() ? tax : 0)).toFixed(2);
                cgstAmt = Number(r.CGSTAmt || (state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2) : 0)).toFixed(2);
                sgstAmt = Number(r.SGSTAmt || (state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2) : 0)).toFixed(2);
                igstAmt = Number(r.IGSTAmt || (state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount : 0)).toFixed(2);
            }

            return {
                ...r,
                productId: r.productId || r.Id || r.id || '',
                UnitId: matchedUnit ? String(matchedUnit.UnitId) : (product?.UnitId || ''),
                unit: matchedUnit ? matchedUnit.unit : (product?.Unit || ''),
                qty,
                dispQty: Number(r.dispQty ?? r.qty) || 0,
                rate,
                disc,
                tax,
                TaxableAmt: taxableAmt.toFixed(2),
                taxAmount: Number(r.taxAmount) || taxAmount.toFixed(2),
                amount: taxableAmt.toFixed(2),
                CGST: cgst,
                SGST: sgst,
                IGST: igst,
                CGSTAmt: cgstAmt,
                SGSTAmt: sgstAmt,
                IGSTAmt: igstAmt,
                UTGST: String(r.UTGST || '0.00'),
                GSTCess: String(r.GSTCess || '0.00'),
                TaxId: taxId,
                srNo: srNoValue,
                challanDtsID: r.challanDtsID || null,
                challanID: r.challanID || null,
                soId: r.soId || null,
                // "Enable Convesrion effect in Transaction?" — re-derive these UI-only fields from
                // the product's CURRENT registered defaults (same as handleSelect),
                // since they're never persisted and a saved row's editRow spread
                // would otherwise leave defaultUnitId unset, silently disabling the
                // Unit-change conversion prompt in edit mode. baseRate stays the
                // ORIGINAL pre-conversion rate: r.ConversionRate when a conversion
                // was active on this saved row, else the row's own rate.
                defaultUnitId: matchedUnit ? String(matchedUnit.UnitId) : (product?.UnitId ? String(product.UnitId) : ''),
                convUnitId: product?.ConvUnit ? String(product.ConvUnit) : '',
                productConvQty: Number(product?.ConvQty) || '',
                baseRate: r.ConversionRate != null ? Number(r.ConversionRate) : rate,
                soDtsID: r.soDtsID || null, poQty: r.poQty || null, ex4: r.ex4 || null, ex5: r.ex5 || null, exQty6: r.exQty6 || null, serialNo: r.serialNo || null, oth4: r.oth4 || null, oth5: r.oth5 || null, oth6: r.oth6 || null, oth7: r.oth7 || null };
        });

        setRows(initialRows);
        setNextSrNo(initialRows.length + 1);
        setIsInitialized(true);
        hasInitializedRef.current = true; // â† for lifting rows state up 
    }, [initialData?.tableData, unitOptions, allProducts, state, compState, isInitialized]);

    useEffect(() => {
        // `current` was just loaded from a saved row (editRow) — skip this one
        // pass so the DB-stored amount isn't immediately overwritten before the
        // user has actually changed anything.
        if (skipNextCalcRef.current) {
            skipNextCalcRef.current = false;
            return;
        }

        const qty = Number(current.qty) || 0;
        const rate = Number(current.rate) || 0;
        const disc = Number(current.disc) || 0;
        const tax = Number(current.tax) || 0;
        const baseTaxableAmt = getAmountCalcSafe(qty, rate, current.pkg, disc, isApplyPkgInAmtEnabled, calculateAmount).taxable;
        // Oth1→Oth7 are re-applied on top of the base TaxableAmt here too, so a
        // qty/rate/disc edit doesn't silently wipe out an already-set Oth adjustment.
        const { taxableAmt, amounts: othAmounts } = applyOthCharges(baseTaxableAmt, current);
        const taxAmount = taxableAmt * (tax / 100);
        setCurrent(prev => ({
            ...prev,
            ...othAmounts,
            TaxableAmt: taxableAmt.toFixed(2),
            amount: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00'
        }));
    }, [current.qty, current.rate, current.pkg, current.disc, current.tax, current.oth1, current.oth2, current.oth3, current.oth4, current.oth5, current.oth6, current.oth7, current.oth1Amt, current.oth2Amt, current.oth3Amt, current.oth4Amt, current.oth5Amt, current.oth6Amt, current.oth7Amt, state, compState, isApplyPkgInAmtEnabled, calculateAmount]);

    // Recalculate Rate from MRP when GST (TaxId) changes, if Apply MRP in Purchase is enabled
    useEffect(() => {
        if (!isApplyMRPInPurchaseEnabled || !current.MRP || Number(current.MRP) <= 0) return;

        const matchedGST = gsts.find(g => String(g.Id) === String(current.TaxId));
        if (!matchedGST) return;

        const tax = getTaxPercent(matchedGST.value);
        const newRate = calculatePurchaseRateFromMRP(Number(current.MRP), tax);
        const taxableAmt = current.qty * newRate * (1 - current.disc / 100);
        const taxAmount = taxableAmt * (tax / 100);

        console.log('🔄 DebitNote: GST changed, recalculating Rate from MRP:', { MRP: current.MRP, tax, newRate });

        setCurrent(prev => ({
            ...prev,
            rate: newRate,
            tax,
            amount: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00',
            TaxableAmt: taxableAmt.toFixed(2)
        }));
    }, [current.TaxId, isApplyMRPInPurchaseEnabled, gsts]);

    useEffect(() => {
        const n = v => Number(v || 0);
        const isSameState = state?.toLowerCase() === compState?.toLowerCase();

        let basicAmountSum = 0;
        let grossAmountSum = 0;
        let discountTotal = 0;
        let cgstTotal = 0;
        let sgstTotal = 0;
        let igstTotal = 0;

        const regularRows = rows.filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300);

        regularRows.forEach(r => {
            const qty = n(r.qty);
            const rate = n(r.rate);
            const gross = getAmountCalcSafe(qty, rate, r.pkg, 0, isApplyPkgInAmtEnabled, calculateAmount).gross;
            grossAmountSum += gross;
            const taxable = n(r.amount || r.TaxableAmt);
            // Disc% is against gross only — independent of any Oth1-7
            // adjustment baked into `taxable`, which is the final amount.
            const discAmtRow = gross * (n(r.disc) / 100);
            const taxPct = n(r.tax);

            const cgstAmtRow = (r.CGSTAmt != null && r.CGSTAmt !== '') ? n(r.CGSTAmt) : (isSameState ? taxable * (taxPct / 100) / 2 : 0);
            const sgstAmtRow = (r.SGSTAmt != null && r.SGSTAmt !== '') ? n(r.SGSTAmt) : (isSameState ? taxable * (taxPct / 100) / 2 : 0);
            const igstAmtRow = (r.IGSTAmt != null && r.IGSTAmt !== '') ? n(r.IGSTAmt) : (isSameState ? 0 : taxable * (taxPct / 100));

            basicAmountSum += taxable;
            discountTotal += discAmtRow;
            cgstTotal += cgstAmtRow;
            sgstTotal += sgstAmtRow;
            igstTotal += igstAmtRow;
        });

        const totalTax = cgstTotal + sgstTotal + igstTotal;

        // Additional Charges must be folded in before Round Off so roundOff reflects them
        const chargesTotal = n(chargesSummary.total) + n(chargesSummary.cgst) + n(chargesSummary.sgst) + n(chargesSummary.igst);
        const finalAmount = basicAmountSum + totalTax + chargesTotal;

        // const roundedFinal = Math.ceil(finalAmount);
        // const roundOff = Number((roundedFinal - finalAmount).toFixed(2));
        const decimalPart = finalAmount - Math.floor(finalAmount);

        const roundedFinal =
            decimalPart >= 0.5
                ? Math.ceil(finalAmount)
                : Math.floor(finalAmount);

        const roundOff = Number((roundedFinal - finalAmount).toFixed(2));

        setTotals({
            basic: basicAmountSum.toFixed(2),
            grossBasic: grossAmountSum.toFixed(2),
            discount: discountTotal.toFixed(2),
            cgst: cgstTotal.toFixed(2),
            sgst: sgstTotal.toFixed(2),
            igst: igstTotal.toFixed(2),
            roundOff: roundOff.toFixed(2),
            total: roundedFinal.toFixed(2)
        });
    }, [rows, state, compState, chargesSummary]);

    // Additional Charges: given a charge and its current popup input, compute
    // the charge amount, the tax on it (only when TaxEnabled), and the final
    // (tax-inclusive) amount — this is what "Final Amount" displays live.
    const getChargeFinalAmounts = (charge, input) => {
        const chargeAmount = Number(input?.amount) || 0;
        if (!charge.TaxEnabled) {
            return { chargeAmount, taxPercent: 0, taxAmount: 0, finalAmount: chargeAmount };
        }
        const selectedGST = gsts.find(g => String(g.Id) === String(input?.taxId));
        const taxPercent = parseFloat(selectedGST?.value) || 0;
        const taxAmount = chargeAmount * taxPercent / 100;
        return { chargeAmount, taxPercent, taxAmount, finalAmount: chargeAmount + taxAmount };
    };

    // Additional Charges: on Save, fold every charge's final amount into the
    // Total Amount shown in the summary, and its tax (if any) into CGST+SGST
    // (same state) or IGST (different state) — same split rule the totals
    // useEffect above already uses for product rows.
    const handleSaveCharges = () => {
        const isSameState = state?.toLowerCase() === compState?.toLowerCase();
        let addTotal = 0, addCgst = 0, addSgst = 0, addIgst = 0;
        additionalCharges.forEach(charge => {
            const key = charge.TransType;
            const input = chargeInputs[key];
            if (!input || !input.amount) return;
            const amount = Number(input.amount) || 0;
            const { taxAmount } = getChargeFinalAmounts(charge, input);
            addTotal += amount;
            if (charge.TaxEnabled) {
                if (isSameState) {
                    addCgst += taxAmount / 2;
                    addSgst += taxAmount / 2;
                } else {
                    addIgst += taxAmount;
                }
            }
        });
        setChargesSummary({ total: addTotal, cgst: addCgst, sgst: addSgst, igst: addIgst });
        setChargesDialogOpen(false);
    };

    // const handleChange = field => e => {
    //     if (field === 'UnitId') {
    //         const selectedUnit = unitOptions.find(u => u.UnitId === Number(e.target.value));
    //         setCurrent(prev => ({
    //             ...prev,
    //             UnitId: e.target.value,
    //             unit: selectedUnit ? selectedUnit.unit : ''
    //         }));
    //     } else {
    //         setCurrent(prev => ({ ...prev, [field]: e.target.value }));
    //     }
    // };

    const handleChange = field => e => {
        if (field === 'UnitId') {
            const selectedUnit = unitOptions.find(u => u.UnitId === Number(e.target.value));
            const newUnitId = e.target.value;

            // "Enable Convesrion effect in Transaction?" — only intervene when the config is on
            // and the newly-picked unit differs from this product's own default
            // Unit (current.defaultUnitId, captured at select time). Changing
            // back to the default reverts immediately with no popup (see below).
            if (isApplyRateOnConversionEnabled && current.defaultUnitId && String(newUnitId) !== String(current.defaultUnitId)) {
                setPendingUnitChange({
                    UnitId: newUnitId,
                    unit: selectedUnit ? selectedUnit.unit : '',
                    previousUnitId: current.UnitId,
                });
                // Pre-fill per spec: the product's registered ConvQty only when the
                // newly-picked unit matches the product's registered ConvUnit;
                // otherwise default to 1 (no known ratio for an unrelated unit).
                const matchesConvUnit = current.convUnitId && String(newUnitId) === String(current.convUnitId);
                setConversionQtyInput(matchesConvUnit && current.productConvQty ? String(current.productConvQty) : '1');
                setConversionQtyDialogOpen(true);
                return;
            }

            // Reverting to the product's own default Unit (or config is off) —
            // behave exactly as before, and clear any previously-active conversion.
            setCurrent(prev => ({
                ...prev,
                UnitId: newUnitId,
                unit: selectedUnit ? selectedUnit.unit : '',
                ConversionQty: null,
                ConversionRate: null,
                rate: prev.defaultUnitId && String(newUnitId) === String(prev.defaultUnitId) ? prev.baseRate : prev.rate,
            }));
        } else if (field === 'qty') {
            let value = e.target.value;
            let numeric = Number(value) || 0;

            setCurrent(prev => {
                const updated = {
                    ...prev,
                    qty: numeric,
                    dispQty: numeric
                };
                // Qty shifts the base amount Oth1-7 are computed from — reapply
                // them live here too, same as Disc already does via handleBaseFieldChange.
                return { ...updated, ...recomputeTaxable(updated) };
            });
        } else if (field === 'rate' || field === 'pkg') {
            // Rate/Pkg also shift the base amount Oth1-7 are computed from —
            // reapply them live here too, same as Disc already does via handleBaseFieldChange.
            setCurrent(prev => {
                const updated = { ...prev, [field]: e.target.value };
                return { ...updated, ...recomputeTaxable(updated) };
            });
        } else {
            setCurrent(prev => ({ ...prev, [field]: e.target.value }));
        }
    };

    // "Enable Convesrion effect in Transaction?" — Confirm applies the pending Unit change plus
    // the Conversion Qty the user entered/accepted, deriving the billed Rate from
    // this row's baseRate (the rate as it stood before any conversion — captured
    // once at product-select time and preserved untouched so it can always be
    // recovered). Cancel reverts the Unit selection back to whatever it was
    // before, so a cancelled popup never leaves the row half-changed.
    const handleConfirmConversionQty = () => {
        if (!pendingUnitChange) return;
        const convQty = Number(conversionQtyInput) || 0;

        if (convQty > 0) {
            const { rate: convertedRate } = calculateConversion(current.baseRate, current.qty, convQty);
            setCurrent(prev => ({
                ...prev,
                UnitId: pendingUnitChange.UnitId,
                unit: pendingUnitChange.unit,
                rate: convertedRate,
                ConversionQty: convQty,
                ConversionRate: prev.baseRate,
            }));
        } else {
            // Invalid/blank conversion qty — treat like Cancel rather than divide by zero.
            setCurrent(prev => ({ ...prev, UnitId: pendingUnitChange.previousUnitId }));
        }

        setConversionQtyDialogOpen(false);
        setPendingUnitChange(null);
        setConversionQtyInput('');
    };

    const handleCancelConversionQty = () => {
        if (pendingUnitChange) {
            setCurrent(prev => ({ ...prev, UnitId: pendingUnitChange.previousUnitId }));
        }
        setConversionQtyDialogOpen(false);
        setPendingUnitChange(null);
        setConversionQtyInput('');
    };


    const handleSelect = async product => {
        console.log('🔍 Product selected - Full product object:', product);
        console.log('🔍 Product.MRP:', product.MRP);

        const matchedUnit = unitOptions.find(opt => {
            if (product.UnitId && opt.UnitId === product.UnitId) return true;
            if (product.Unit && opt.unit?.toLowerCase() === product.Unit.toLowerCase()) return true;
            if (product.unit && opt.unit?.toLowerCase() === product.unit.toLowerCase()) return true;
            return false;
        });
        const qty = 1;
        let rate = Number(product.Rate) || 0;
        let disc = Number(product.Discount) || 0;
        let MRP = Number(product.MRP) || '';
        console.log('💰 MRP value extracted:', MRP);

        // Fetch last sale rate if enabled
        console.log('📦 DebitNote: Product selected, checking if last sale rate should be fetched');
        if (isLastSaleRateEnabled) {
            console.log('💾 DebitNote: Fetching last sale rate for product:', product.Id, 'party:', clientData?.clientId);
            const lastSaleData = await getLastSaleRate(product.Id, clientData?.clientId);
            if (lastSaleData.rate !== null) {
                rate = lastSaleData.rate;
                console.log('✅ DebitNote: Last sale rate fetched:', rate);
            }
            if (lastSaleData.disc !== null) {
                disc = lastSaleData.disc;
                console.log('✅ DebitNote: Last sale disc fetched:', disc);
            }
        }

        const tax = parseFloat(product.GST) || 0;
        const taxId = product.TaxId || null;
        // "Disc Scheme" — Disc is taken from clientmaster.Disc; Oth1/Oth2 from clientmaster.Disc1/Disc2.
        // Oth1 and Oth2 are checked independently: each is filled only if it is
        // enabled, Percentage (not Rupees) and minus (AddLess). Empty/null -> 0.
        let discSchemeOth = null;
        if (isDiscSchemeEnabled && clientData?.clientId) {
            const scheme = await getClientDiscScheme(clientData.clientId);
            disc = scheme.disc || 0;
            const oth1Qualifies = isColVisible(COL.OTH1) && !isOthRupeesMode(COL.OTH1) && Boolean(getColRow(COL.OTH1)?.AddLess);
            const oth2Qualifies = isColVisible(COL.OTH2) && !isOthRupeesMode(COL.OTH2) && Boolean(getColRow(COL.OTH2)?.AddLess);
            if (oth1Qualifies || oth2Qualifies) {
                discSchemeOth = {
                    oth1: oth1Qualifies ? String(scheme.disc1 || 0) : '',
                    oth2: oth2Qualifies ? String(scheme.disc2 || 0) : '',
                };
            }
        }

        const taxableAmt = qty * rate * (1 - disc / 100);
        const taxAmount = taxableAmt * (tax / 100);
        setCurrent({
            Id: product.Id?.toString() || '',
            productCode: product.itemCode || '',
            description: product.description || '',
            UnitId: matchedUnit ? String(matchedUnit.UnitId) : '',
            unit: matchedUnit ? matchedUnit.unit : '',
            qty,
            rate,
            disc,
            tax,
            MRP,
            batchNo: '',
            expDate: '',
            serialNo: '',
            amount: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            remark: '',
            TaxId: taxId,
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            UTGST: '0.00',
            GSTCess: '0.00',
            CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00',
            TaxableAmt: taxableAmt.toFixed(2),
            challanDtsID: null,
            challanID: null,
            soId: null,
            soDtsID: null,
            refNo: '',
            refDate: '',
            refQty: '',
            type: '',
            size: '',
            color: '',
            freeQty: '',
            per: '',
            pkg: '',
            oth1: discSchemeOth ? discSchemeOth.oth1 : '',
            oth2: discSchemeOth ? discSchemeOth.oth2 : '',
            oth3: '',
            oth4: '',
            oth5: '',
            oth6: '',
            oth7: '',
            GSTOth: '0.00',
            // "Enable Convesrion effect in Transaction?" — captured from productmaster at select
            // time; no conversion is active yet on a freshly-selected row.
            defaultUnitId: matchedUnit ? String(matchedUnit.UnitId) : '',
            convUnitId: product.ConvUnit ? String(product.ConvUnit) : '',
            productConvQty: Number(product.ConvQty) || '',
            baseRate: rate,
            ConversionQty: null,
            ConversionRate: null
        });
        // Reapply Oth1-7 on the fresh row so Oth1/Oth2 Amount and TaxableAmt reflect the Disc Scheme.
        if (discSchemeOth) {
            setCurrent(prev => ({ ...prev, ...recomputeTaxable(prev) }));
        }
        // setEditIndex(null);
        setErrors({});
    };

    const addProduct = async () => {
        const errs = {};
        const messages = new Set();
        const requiredFields = ['productCode', 'description', 'UnitId', 'qty', 'rate', 'tax'];

        requiredFields.forEach(field => {
            if (!current[field] && current[field] !== 0) {
                errs[field] = 'Required';
                messages.add('All fields are required.');
            }
        });

         //GST validation
          if (!current.TaxId) {
            errs.TaxId = true;
            messages.add('Please select GST before adding product.');
        }


        if (!current.qty || Number(current.qty) <= 0) {
            errs.qty = 'Must be > 0';
            messages.add('Qty must be greater than 0');
        }

        if (!current.rate || Number(current.rate) <= 0) {
            errs.rate = 'Rate must be greater than 0';
            messages.add('Rate must be greater than 0');
        }

        if (current.disc < 0) {
            errs.disc = 'Cannot be negative';
            messages.add('Discount cannot be negative');
        }

        if (current.disc > 100) {
            errs.disc = 'Cannot be more than 100';
            messages.add('Discount cannot be more than 100');
        }

        if (messages.size > 0) {
            errs.global = Array.from(messages);
            setErrors(errs);
            return;
        }

        const safeDisc =
            current.disc === '' || current.disc == null
                ? 0
                : Number(current.disc);

        const qty = Number(current.qty) || 0;

        // Stock validation check
        console.log('📋 DebitNote: About to validate stock for item:', { itemId: current.Id, qty, L_No });
        const stockValidation = await validateMinLevel(current.Id, qty, L_No);
        console.log('📊 DebitNote: Stock validation result:', stockValidation);

        if (stockValidation.shouldShowWarning) {
            console.log('⚠️ DebitNote: Showing stock warning dialog');
            const confirmed = await new Promise((resolve) => {
                setStockWarningData(stockValidation);
                setStockConfirmResolve(() => resolve);
                setStockWarningOpen(true);
            });
            console.log('✅ DebitNote: User response to warning:', confirmed);
            if (!confirmed) {
                console.log('❌ DebitNote: User cancelled - not adding product');
                return;
            }
        } else {
            console.log('✅ DebitNote: No stock warning needed - proceeding');
        }
        const rate = Number(current.rate) || 0;
        // const disc = Number(current.disc) || 0;
        const disc = safeDisc || 0;
        const tax = Number(current.tax) || 0;
        const baseTaxableAmt = getAmountCalcSafe(qty, rate, current.pkg, disc, isApplyPkgInAmtEnabled, calculateAmount).taxable;
        // Oth1→Oth7 are applied on top of this base TaxableAmt before the row is committed,
        // so Add/Update Product always carries the Oth-adjusted amount, not the raw base.
        const { taxableAmt, amounts: othAmounts } = applyOthCharges(baseTaxableAmt, current);
        const taxAmount = taxableAmt * (tax / 100);
        const dispatchQty = Number(qty);

        const row = {
            ...current,
            ...othAmounts,
            productId: current.Id,
            qty,
            dispQty: dispatchQty,
            rate,
            disc,
            tax,
            remark: current.remark || '',
            TaxableAmt: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            amount: taxableAmt.toFixed(2),
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00',
            UTGST: current.UTGST,
            GSTCess: current.GSTCess,
            TaxId: current.TaxId,
            srNo: editIndex != null ? rows[editIndex].srNo : nextSrNo,
            challanDtsID: editIndex != null ? rows[editIndex].challanDtsID : null,
            challanID: editIndex != null ? rows[editIndex].challanID : null,
            soId: editIndex != null ? rows[editIndex].soId : null,
            soDtsID: editIndex != null ? rows[editIndex].soDtsID : null,
            // "Enable Convesrion effect in Transaction?" — carry the active conversion (if any)
            // onto the saved row so it survives into payloadRows / the backend.
            ConversionQty: current.ConversionQty != null ? Number(current.ConversionQty) : null,
            ConversionRate: current.ConversionRate != null ? Number(current.ConversionRate) : null,
            TransType: current.TransType || 100
        };

        const existingRowIndex = rows.findIndex(
            (r, i) => r.productCode === current.productCode && i !== editIndex
        );

        // if (existingRowIndex !== -1 && editIndex == null) {
        //     setRows(prev =>
        //         prev.map((r, i) => {
        //             if (i === existingRowIndex) {
        //                 const newQty = Number(r.qty) + qty;
        //                 const newTaxableAmt = newQty * rate * (1 - disc / 100);
        //                 const newTaxAmount = newTaxableAmt * (tax / 100);
        //                 return {
        //                     ...r,
        //                     qty: newQty,
        //                     rate,
        //                     disc,
        //                     tax,
        //                     remark: current.remark || r.remark,
        //                     TaxableAmt: newTaxableAmt.toFixed(2),
        //                     taxAmount: newTaxAmount.toFixed(2),
        //                     amount: newTaxableAmt.toFixed(2),
        //                     CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
        //                     SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
        //                     IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
        //                     CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (newTaxAmount / 2).toFixed(2) : '0.00',
        //                     SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (newTaxAmount / 2).toFixed(2) : '0.00',
        //                     IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? newTaxAmount.toFixed(2) : '0.00',
        //                     UTGST: current.UTGST,
        //                     GSTCess: current.GSTCess,
        //                     TaxId: current.TaxId,
        //                     challanDtsID: r.challanDtsID,
        //                     challanID: r.challanID,
        //                     soId: r.soId,
        //                     soDtsID: r.soDtsID
        //                 };
        //             }
        //             return r;
        //         })
        //     );
        // } else {
        //     if (editIndex != null) {
        //         setRows(prev => prev.map((r, i) => (i === editIndex ? row : r)));
        //     } else {
        //         setRows(prev => [row, ...prev]);
        //         setNextSrNo(prev => prev + 1);
        //     }
        // }

        if (editIndex != null) {
            setRows(prev => prev.map((r, i) => i === editIndex ? { ...row, srNo: rows[editIndex].srNo } : r));
        } else {
            setRows(prev => [row, ...prev]);
            setNextSrNo(prev => prev + 1);
        }

        setIsEditingMode(false);
        setCurrent({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, MRP: '', remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', GSTCess: '0.00', CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', taxAmount: '', amount: '', TaxId: null, challanDtsID: null, challanID: null, soId: null, soDtsID: null, refNo: '', refDate: '', refQty: '', type: '', size: '', color: '', freeQty: '', per: '', pkg: '', oth1: '', oth2: '', oth3: '', oth4: '', oth5: '', oth6: '', oth7: '', GSTOth: '0.00', TransType: 100 });
        setErrors({});
        setEditIndex(null);
        setOriginalEditQty(null);

        // Focus on Product Code field after adding product (not after updating)
        if (editIndex == null) {
            setTimeout(() => {
                productCodeRef.current?.focus?.();
            }, 0);
        }
    };

    const editRow = idx => {
        const row = rows[idx];
        const matchedUnit = unitOptions.find(u => String(u.UnitId) === String(row.UnitId)) ||
            unitOptions.find(u => String(u.UnitId) === String(row.unit)) ||
            unitOptions.find(u => u.unit?.toLowerCase() === String(row.unit).toLowerCase());

        // This row's Qty/Rate/Pkg/Disc are about to be loaded into `current` from
        // the DB values — skip the next live-calculation pass so it doesn't
        // immediately overwrite the stored amount before the user edits anything.
        skipNextCalcRef.current = true;

        setCurrent({
            ...row,
            Id: row.productId,
            UnitId: matchedUnit ? String(matchedUnit.UnitId) : row.UnitId || null,
            unit: matchedUnit ? matchedUnit.unit : row.unit || '',
            CGST: String(row.CGST || '0.00'),
            SGST: String(row.SGST || '0.00'),
            IGST: String(row.IGST || '0.00'),
            CGSTAmt: String(row.CGSTAmt || '0.00'),
            SGSTAmt: String(row.SGSTAmt || '0.00'),
            IGSTAmt: String(row.IGSTAmt || '0.00'),
            UTGST: String(row.UTGST || '0.00'),
            GSTCess: String(row.GSTCess || '0.00'),
            TaxableAmt: String(row.TaxableAmt || '0.00'),
            taxAmount: String(row.taxAmount || '0.00'),
            amount: String(row.amount || '0.00'),
            TaxId: row.TaxId || null,
            challanDtsID: row.challanDtsID || null,
            challanID: row.challanID || null,
            soId: row.soId || null,
            soDtsID: row.soDtsID || null
        });
        setEditIndex(idx);
        setOriginalEditQty(Number(row.qty) || 0);
        setIsEditingMode(true);
    };

    const deleteRow = idx => {
        setRows(prev => prev.filter((_, i) => i !== idx));
        if (idx === editIndex) {
            setCurrent({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, MRP: '', remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', GSTCess: '0.00', CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', taxAmount: '', amount: '', TaxId: null, challanDtsID: null, challanID: null, soId: null, soDtsID: null, refNo: '', refDate: '', refQty: '', type: '', size: '', color: '', freeQty: '', per: '', pkg: '', oth1: '', oth2: '', oth3: '', oth4: '', oth5: '', oth6: '', oth7: '', GSTOth: '0.00', TransType: 100 });
            setEditIndex(null);
            setErrors({});
            setOriginalEditQty(null);
            setIsEditingMode(false);
        }
    };

    const handleNext = () => {
        if (rows.length === 0) {
            setErrors({ global: 'Add at least one product' });
            return;
        }

        const isSameState = state?.toLowerCase() === compState?.toLowerCase();

        const payloadRows = rows.map(r => {
            const qty = Number(r.qty) || 0;
            const rate = Number(r.rate) || 0;
            const gross = qty * rate;
            const pkgGross = getAmountCalcSafe(qty, rate, r.pkg, 0, isApplyPkgInAmtEnabled, calculateAmount).gross;
            const taxable = Number(r.amount || r.TaxableAmt) || 0;
            // Disc% is against gross only — independent of any Oth1-7
            // adjustment baked into `taxable`, which is the final amount.
            const discAmt = pkgGross * ((Number(r.disc) || 0) / 100);
            const taxPct = Number(r.tax) || 0;
            const taxAmt = Number(r.taxAmount) || (taxable * (taxPct / 100));

            const cgstAmt = (r.CGSTAmt != null && r.CGSTAmt !== '') ? Number(r.CGSTAmt) : (isSameState ? taxAmt / 2 : 0);
            const sgstAmt = (r.SGSTAmt != null && r.SGSTAmt !== '') ? Number(r.SGSTAmt) : (isSameState ? taxAmt / 2 : 0);
            const igstAmt = (r.IGSTAmt != null && r.IGSTAmt !== '') ? Number(r.IGSTAmt) : (isSameState ? 0 : taxAmt);

            return {
                ...r,
                productId: r.productId,
                productCode: r.productCode,
                description: r.description,
                unit: r.unit,
                UnitId: Number(r.UnitId) || null,
                qty,
                rate,
                MRP: Number(r.MRP) || 0,
                disc: Number(r.disc) || 0,
                tax: taxPct,
                batchNo: r.batchNo || '',
                expDate: r.expDate || null,
                serialNo: r.serialNo || '',
                remark: r.remark || '',
                gross: Number(gross.toFixed(2)),
                amount: Number(taxable.toFixed(2)),
                DiscAmt: Number(discAmt.toFixed(2)),
                taxAmount: Number(taxAmt.toFixed(2)),
                srNo: r.srNo,
                TaxId: r.TaxId || null,
                CGST: isSameState ? Number((taxPct / 2).toFixed(2)) : 0,
                SGST: isSameState ? Number((taxPct / 2).toFixed(2)) : 0,
                IGST: isSameState ? 0 : Number(taxPct.toFixed(2)),
                CGSTAmt: Number(cgstAmt.toFixed(2)),
                SGSTAmt: Number(sgstAmt.toFixed(2)),
                IGSTAmt: Number(igstAmt.toFixed(2)),
                UTGST: r.UTGST || '0.00',
                GSTCess: r.GSTCess || '0.00',
                challanDtsID: r.challanDtsID || null,
                challanID: r.challanID || null,
                soId: r.soId || null,
                soDtsID: r.soDtsID || null,
                refNo: r.refNo || null,
                refDate: r.refDate || null,
                refQty: r.refQty || null,
                type: r.type || null,
                size: r.size || null,
                color: r.color || null,
                freeQty: r.freeQty || null,
                per: r.per || null,
                pkg: r.pkg || null,
                oth1: r.oth1 || null,
                oth2: r.oth2 || null,
                oth3: r.oth3 || null,
                GSTOth: r.GSTOth || null,
                poQty: r.poQty || null,
                ex4: r.ex4 || null,
                ex5: r.ex5 || null,
                exQty6: r.exQty6 || null,
                serialNo: r.serialNo || null,
                oth4: r.oth4 || null,
                oth5: r.oth5 || null,
                oth6: r.oth6 || null,
                oth7: r.oth7 || null,
                // "Enable Convesrion effect in Transaction?" — ConversionQty/ConversionRate mirror
                // what's stored on the row (null when no conversion is active on this
                // row). stockQty is the base-unit-equivalent quantity to post to
                // stockinout's StockInQty/StockOutQty; it equals qty/ConversionQty
                // when a conversion is active on this row, otherwise the plain qty.
                ConversionQty: r.ConversionQty != null ? Number(r.ConversionQty) : null,
                ConversionRate: r.ConversionRate != null ? Number(r.ConversionRate) : null,
                stockQty: r.ConversionQty
                    ? Number(((Number(r.qty) || 0) / Number(r.ConversionQty)).toFixed(2))
                    : qty,
                TransType: r.TransType || 100
            };
        });

        // Additional Charges: charges saved in UserDefineColumns with ColToBeAsk=1
        // (i.e. present in `additionalCharges`, sourced from UserDefineContext) are
        // folded into the debit note payload — Adc{SrNo}Amt/Adc{SrNo}TaxId in summary
        // for all 5 fixed slots, plus one pseudo item row per SELECTED charge
        // (TransType 200-204, SrNo 20000-20004) appended to tableData.
        // When a charge's Tax is disabled, its TaxId is resolved to the taxmaster
        // row where GST = 0 (found via gsts, whose `value` doubles as the % — same
        // pattern already used elsewhere in this file, e.g. parseFloat(opt.value)).
        const zeroTaxId = gsts.find(g => parseFloat(g.value) === 0)?.Id ?? null;

        const additionalChargeSummary = {};
        const additionalChargeItems = [];
        for (let slot = 1; slot <= 5; slot++) {
            const charge = additionalCharges.find(c => Number(c.SrNo) === slot);
            if (!charge) {
                additionalChargeSummary[`Adc${slot}Amt`] = 0;
                additionalChargeSummary[`Adc${slot}TaxId`] = null;
                continue;
            }
            const key = charge.TransType;
            const input = chargeInputs[key] || { amount: '', taxId: charge.TaxId ?? null };
            const { taxPercent, taxAmount, finalAmount } = getChargeFinalAmounts(charge, input);
            const amount = Number(input.amount) || 0;
            const taxId = charge.TaxEnabled ? (input.taxId ?? null) : zeroTaxId;

            additionalChargeSummary[`Adc${slot}Amt`] = amount;
            additionalChargeSummary[`Adc${slot}TaxId`] = taxId;

            // The item row (debitnotedetails) is now shaped exactly like a normal
            // product row (same field set as payloadRows above) instead of a small
            // subset — CGST/SGST/IGST hold the % rate, CGSTAmt/SGSTAmt/IGSTAmt hold
            // the tax amounts, TaxableAmt holds the tax-inclusive final amount (e.g.
            // 100 + 18% tax = 118), and every other normal-row field gets a sensible
            // default/analog for a charge line (no productCode/qty/rate in the usual
            // sense, so ChargeName stands in for productCode/description, qty=1,
            // rate=amount so gross/amount line up the same way a real row's do).
            const chargeItemId = getChargeItemId('DEBIT', slot);
            additionalChargeItems.push({
                Id: chargeItemId,
                productId: chargeItemId,
                productCode: charge.ChargeName || '',
                description: charge.ChargeName || '',
                UnitId: null,
                unit: '',
                qty: 1,
                rate: amount,
                disc: 0,
                tax: Number(taxPercent.toFixed(2)),
                batchNo: '',
                expDate: null,
                serialNo: '',
                amount: amount,
                taxAmount: Number(taxAmount.toFixed(2)),
                remark: '',
                TaxId: taxId,
                CGST: isSameState ? Number((taxPercent / 2).toFixed(2)) : 0,
                SGST: isSameState ? Number((taxPercent / 2).toFixed(2)) : 0,
                IGST: isSameState ? 0 : Number(taxPercent.toFixed(2)),
                UTGST: '0.00',
                GSTCess: '0.00',
                GSTOth: '0.00',
                GSTEduCess: '0.00',
                GSTEducessAmt: '0.00',
                CGSTAmt: isSameState ? Number((taxAmount / 2).toFixed(2)) : 0,
                SGSTAmt: isSameState ? Number((taxAmount / 2).toFixed(2)) : 0,
                IGSTAmt: isSameState ? 0 : Number(taxAmount.toFixed(2)),
                GSTCEssAmt: '0.00',
                TaxableAmt: Number(finalAmount.toFixed(2)),
                challanDtsID: null,
                challanID: null,
                soId: null,
                soDtsID: null,
                PONo: null,
                PODate: null,
                poQty: null,
                type: null,
                size: null,
                color: null,
                freeQty: null,
                per: null,
                pkg: null,
                oth1: null,
                oth2: null,
                oth3: null,
                oth4: null,
                oth5: null,
                oth6: null,
                oth7: null,
                ex4: null,
                ex5: null,
                exQty6: null,
                serialNo: '',
                configData: [],
                _isNew: true,
                configType: null,
                applyBatchNo: 0,
                applySerialNo: 0,
                warrantyMonths: null,
                expiryDays: null,
                srNo: 19999 + slot,
                dispQty: 0,
                gross: amount,
                DiscAmt: 0,
                refNo: null,
                refDate: null,
                refQty: null,
                oth1Amt: null,
                oth2Amt: null,
                oth3Amt: null,
                oth4Amt: null,
                oth5Amt: null,
                oth6Amt: null,
                oth7Amt: null,
                // Fixed identifiers driving the backend's TransType-aware SrNo
                // override (see dispatchController.js / debitNoteController.js).
                SrNo: 19999 + slot,
                TransType: 199 + slot,
            });
        }

        const payload = {
            tableData: [...payloadRows, ...additionalChargeItems],
            summary: {
                basic: Number(totals.basic),
                grossBasic: Number(totals.grossBasic),
                discount: Number(totals.discount),
                cgst: Number(totals.cgst) + Number(chargesSummary.cgst || 0),
                sgst: Number(totals.sgst) + Number(chargesSummary.sgst || 0),
                igst: Number(totals.igst) + Number(chargesSummary.igst || 0),
                roundOff: Number(totals.roundOff),
                total: Number(totals.total),
                ...additionalChargeSummary
            },
            chargeInputs: chargeInputs
        };


        console.log("payload in handleNext", payload)

        onNext(payload);
    };

    const formatINR = val => {
        const num = Number(val) || 0;
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const columns = [
        { field: 'srNo', headerName: '#', width: 38, align: 'center', headerAlign: 'center' },
        {
            field: 'product', headerName: 'Product', flex: 1, sortable: false, filterable: false,
            renderCell: params => {
                const { productCode, description, unit, tax, batchNo, expDate, serialNo, remark, CGSTAmt, SGSTAmt, IGSTAmt, ChallanNumber, ChallanDate } = params.row;
                const isSameState = state?.toLowerCase() === compState?.toLowerCase();
                return (
                    <Box sx={{ py: 0.5, display: 'flex', flexDirection: 'column' }}>
                        <Box>
                            {/* <Typography variant="body2" fontWeight="bold" component="div" sx={{ cursor: 'pointer', color: '#1877F2', textDecoration: 'underline', fontSize: '0.85rem' }}
                                onClick={e => {
                                    e.stopPropagation();
                                    editRow(params.row.index);
                                }}
                            >
                                {productCode}
                            </Typography> */}

                            <Typography
                                variant="body2"
                                fontWeight="bold"
                                component="div"
                                sx={isProductEditLocked
                                    ? { color: 'text.secondary', fontSize: '0.85rem' }
                                    : {
                                        cursor: 'pointer',
                                        color: '#1877F2',
                                        textDecoration: mode === 'ref' ? 'none' : 'underline',
                                        fontSize: '0.85rem'
                                    }}
                                onClick={e => {
                                    if (isProductEditLocked) return;
                                    e.stopPropagation();
                                    editRow(params.row.index);
                                }}
                            >
                                {productCode}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                {description}
                            </Typography>
                        </Box>
                        {(isColVisible(COL.UNIT) || isColVisible(COL.TAX_CODE)) && (
                            <Typography variant="caption" display="block">
                                {isColVisible(COL.UNIT) && `Unit: ${unit || 'N/A'}`}
                                {isColVisible(COL.UNIT) && isColVisible(COL.TAX_CODE) && ' | '}
                                {isColVisible(COL.TAX_CODE) && `GST: ${tax}%`}
                            </Typography>
                        )}
                        {(isSameState ? (isColVisible(COL.SGST) || isColVisible(COL.CGST)) : isColVisible(COL.IGST)) && (
                            <Typography variant="caption" display="block" color="text.secondary">
                                {isSameState
                                    ? [
                                        isColVisible(COL.SGST) && `SGST: ₹${formatINR(SGSTAmt)}`,
                                        isColVisible(COL.CGST) && `CGST: ₹${formatINR(CGSTAmt)}`,
                                    ].filter(Boolean).join(' + ')
                                    : `IGST: ₹${formatINR(IGSTAmt)}`}
                            </Typography>
                        )}
                        {((batchNo && isColVisible(COL.BATCH_NO)) || (expDate && isColVisible(COL.EXP_DATE)) || serialNo) && (
                            <Typography variant="caption" display="block">
                                {batchNo && isColVisible(COL.BATCH_NO) && `Batch: ${batchNo}`}
                                {expDate && isColVisible(COL.EXP_DATE) && ` | Exp: ${expDate}`}
                            </Typography>
                        )}
                        {remark && isColVisible(COL.REMARK) && (<Typography variant="caption" display="block" color="text.secondary"> Remark: {remark} </Typography>)}
                        {OTH_FIELDS.some(({ field, col }) => isColVisible(col) && params.row[othDisplayField(field, col)]) && (
                            <Typography variant="caption" display="block" color="text.secondary">
                                {OTH_FIELDS
                                    .filter(({ field, col }) => isColVisible(col) && params.row[othDisplayField(field, col)])
                                    .map(({ field, col }) => {
                                        const rupees = isOthRupeesMode(col);
                                        const displayVal = params.row[othDisplayField(field, col)];
                                        const amt = params.row[`${field}Amt`];
                                        // Display-only: prefix with "-" when this Oth is configured as
                                        // subtract (AddLess=1) in UserDefineColumns — never "+", purely
                                        // cosmetic, doesn't touch stored values or calculations.
                                        const sign = Boolean(getColRow(col)?.AddLess) ? '-' : '';
                                        return rupees
                                            ? `${getColLabel(col, field)}: ${sign}₹${formatINR(displayVal)}`
                                            : `${getColLabel(col, field)}: ${displayVal}%${amt ? ` (${sign}₹${formatINR(amt)})` : ''}`;
                                    })
                                    .join(' | ')}
                            </Typography>
                        )}
                        {(ChallanNumber || ChallanDate) && (
                            <Typography variant="caption" display="block" color='blue'>
                                {ChallanNumber && `InvoiceNo: ${ChallanNumber}`}
                                {ChallanDate && ` | Date: ${ChallanDate}`}
                            </Typography>
                        )}
                    </Box>
                );
            }
        },
        { field: 'dispQty', headerName: 'Qty', width: 50, align: 'center', headerAlign: 'center' },
        { field: 'rate', headerName: 'Rate', width: 100, align: 'right', headerAlign: 'center', renderCell: params => `₹${formatINR(params.row.rate)}` },
        { field: 'disc', headerName: 'Disc (%)', width: 50, align: 'center', headerAlign: 'center' },
        { field: 'amount', headerName: 'Amount (₹)', width: 100, align: 'right', headerAlign: 'right', renderCell: params => `₹${formatINR(Number(params.row.TaxableAmt || 0))}` },
        {
            width: 30, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
            renderCell: params => (
                <IconButton
                    size="small"
                    onClick={() => deleteRow(params.row.index)}
                    color="error"
                    disabled={false}
                    sx={{ opacity: 1 }}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )
        }
    ];

    return (
        <Paper sx={{ p: 1, overflowX: 'hidden', mt: -1.5 }}>
            <Box display="flex" flexDirection={isSmall ? 'column' : 'row'} gap={2} sx={{ width: '100%', boxSizing: 'border-box', flexWrap: isSmall ? 'wrap' : 'nowrap', overflow: 'hidden' }} >
                <Box display="flex" flexDirection="column" gap={2} sx={{ width: '100%', maxWidth: isSmall ? '100%' : 420, boxSizing: 'border-box', p: 2, borderRight: isSmall ? 'none' : '1px solid #e0e0e0', flex: { lg: '1 1 100%' }, }} >
                    <Box>
                        <DropdownProductCode ref={productCodeRef} id="productCode" options={allProducts} value={current.productCode} onSelect={handleSelect} onNewProductAdded={loadAllProducts} connectionString={connectionString} searchBy="code" onProductMasterClose={handleProductAdded} autoFocus={!initialData?.tableData?.length} />
                        {!!errors.productCode && (<Typography color="error" variant="caption"> {errors.productCode} </Typography>)}
                    </Box>
                    {/* <TextField label="Description" required fullWidth multiline minRows={1} size="small" placeholder="Enter product description" value={current.description} onChange={handleChange('description')} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiInputBase-root:not(.MuiInputBase-multiline)': { height: 38 }, '& .MuiOutlinedInput-inputMultiline': { padding: '8px 12px', minHeight: '80px' } }} /> */}
                    {isColVisible(COL.DESCRIPTION) && (
                        <DropdownProductCode
                            id="description"
                            label="Description"
                            options={allProducts}
                            value={current.description}
                            onSelect={handleSelect}
                            onNewProductAdded={loadAllProducts}
                            connectionString={connectionString}
                            searchBy="description"   // âœ… IMPORTANT
                            onProductMasterClose={handleProductAdded}
                        />
                    )}
                    {/* {(isColVisible('Ref.No') || isColVisible('Ref. Date') || isColVisible('Ref. Qty')) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible('Ref.No') && (
                                <TextField label="Ref. No" size="small" fullWidth value={current.refNo} onChange={handleChange('refNo')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible('Ref. Date') && (
                                <TextField label="Ref. Date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={current.refDate} onChange={handleChange('refDate')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible('Ref. Qty') && (
                                <TextField label="Ref. Qty" type="number" size="small" fullWidth value={current.refQty} onChange={handleChange('refQty')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )} */}
                    {(isColVisible(COL.TYPE) || isColVisible(COL.SIZE) || isColVisible(COL.COLOR)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.TYPE) && (
                                <TextField label="Type" size="small" fullWidth value={current.type} onChange={handleChange('type')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.SIZE) && (
                                <TextField label="Size" size="small" fullWidth value={current.size} onChange={handleChange('size')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.COLOR) && (
                                <TextField label="Color" size="small" fullWidth value={current.color} onChange={handleChange('color')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    <Box display="flex" flexDirection="row" gap={1.5}>
                        {isColVisible(COL.UNIT) && (
                            <TextField label="Unit" required select fullWidth size='small' value={String(current.UnitId)} onChange={handleChange('UnitId')} disabled={mode === 'ref'} SelectProps={{ native: true }} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} >
                                <option value=""></option>
                                {unitOptions.map(u => (
                                    <option key={u.UnitId} value={u.UnitId}>
                                        {u.unit}
                                    </option>
                                ))}
                            </TextField>
                        )}
                        {/* <TextField label="Tax(%)" fullWidth type="number" disabled value={current.tax} onChange={handleChange('tax')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} /> */}
                        {isColVisible(COL.TAX_CODE) && (
                            <Dropdown
                                id="gst"
                                label="GST"
                                valueKey="Id"
                                labelKey="value"
                                value={current.TaxId ? String(current.TaxId) : ""}
                                options={gsts}
                                required
                                disabled={mode === 'ref'}
                                onChange={(opt) => {
                                    if (!opt) {
                                        setCurrent(prev => ({
                                            ...prev,
                                            tax: 0,
                                            TaxId: null
                                        }));
                                    } else {
                                        const gstValue = parseFloat(opt.value) || 0;

                                        setCurrent(prev => ({
                                            ...prev,
                                            tax: gstValue,   // % value used in calculation
                                            TaxId: opt.Id    // backend id
                                        }));
                                    }
                                }}
                                 error={!!errors.TaxId}
                            />
                        )}
                    </Box>
                    {(isColVisible(COL.BATCH_NO) || isColVisible(COL.EXP_DATE)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.BATCH_NO) && (
                                <TextField label="Batch No" size="small" fullWidth value={current.batchNo} onChange={handleChange('batchNo')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.EXP_DATE) && (
                                <TextField label="Exp. Date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={current.expDate} onChange={handleChange('expDate')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {isApplyMRPInPurchaseEnabled ? (
                        // When Apply MRP in Purchase is enabled: Qty, MRP, Rate, Disc in single row
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {isColVisible(COL.QTY) && (
                                <Box sx={{ flex: 0.75, minWidth: 60, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                    <TextField
                                        label="Qty"
                                        required
                                        size='small'
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0, step: 'any' }}
                                        value={current.qty}
                                        onChange={handleChange('qty')}
                                        error={!!errors.qty}
                                        helperText={errors.qty}
                                        InputLabelProps={{ sx: requiredAsteriskStyle }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }}
                                    />
                                </Box>
                            )}
                            {/* MRP Field - visible when Apply MRP is enabled */}
                            <Box sx={{ flex: 1, minWidth: 60, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                <TextField
                                    label="MRP"
                                    size='small'
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    value={current.MRP}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const mrpValue = Number(val) || 0;

                                        setCurrent(prev => ({
                                            ...prev,
                                            MRP: val === '' ? '' : mrpValue
                                        }));

                                        if (mrpValue > 0 && current.tax > 0) {
                                            const newRate = calculatePurchaseRateFromMRP(mrpValue, current.tax);
                                            const taxableAmt = current.qty * newRate * (1 - current.disc / 100);
                                            const taxAmount = taxableAmt * (current.tax / 100);

                                            setCurrent(prev => ({
                                                ...prev,
                                                rate: newRate,
                                                amount: taxableAmt.toFixed(2),
                                                taxAmount: taxAmount.toFixed(2),
                                                CGST: state?.toLowerCase() === compState?.toLowerCase() ? (current.tax / 2).toFixed(2) : '0.00',
                                                SGST: state?.toLowerCase() === compState?.toLowerCase() ? (current.tax / 2).toFixed(2) : '0.00',
                                                IGST: state?.toLowerCase() !== compState?.toLowerCase() ? current.tax.toFixed(2) : '0.00',
                                                CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
                                                SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
                                                IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00',
                                                TaxableAmt: taxableAmt.toFixed(2)
                                            }));
                                        }
                                    }}
                                    InputLabelProps={{ sx: requiredAsteriskStyle }}
                                    sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }}
                                />
                            </Box>
                            {isColVisible(COL.RATE) && (
                                <Box sx={{ flex: 1, minWidth: 60, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                    <TextField
                                        label="Rate"
                                        required
                                        size='small'
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0 }}
                                        value={current.rate}
                                        onChange={handleChange('rate')}
                                        disabled={true}
                                        InputLabelProps={{ sx: requiredAsteriskStyle }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }}
                                    />
                                </Box>
                            )}
                            {isColVisible(COL.DISC) && (
                                <Box sx={{ flex: 0.75, minWidth: 60, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                    <TextField
                                        label="Disc%"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0, max: 100 }}
                                        value={current.disc}
                                        onChange={handleChange('disc')}
                                        InputLabelProps={{ sx: requiredAsteriskStyle }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }}
                                    />
                                </Box>
                            )}
                        </Box>
                    ) : (
                        // When Apply MRP is not enabled: Qty, Rate, Disc in separate columns (original layout)
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.QTY) && (
                                <TextField label="Qty" required size='small' fullWidth type="number" inputProps={{ min: 0, step: 'any' }} value={current.qty} onChange={handleChange('qty')} error={!!errors.qty} helperText={errors.qty} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.RATE) && (
                                <TextField label="Rate" required size='small' fullWidth type="number" inputProps={{ min: 0 }} value={current.rate} onChange={handleChange('rate')} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.DISC) && (
                                <TextField label="Disc%" size='small' fullWidth type="number" inputProps={{ min: 0, max: 100 }} value={current.disc} onChange={handleChange('disc')} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {(isColVisible(COL.PKG) || isColVisible(COL.FREE_QTY) || isColVisible(COL.PER)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.PKG) && (
                                <TextField label="Pkg" size="small" fullWidth value={current.pkg || ''} onChange={handleChange('pkg')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.FREE_QTY) && (
                                <TextField label="Free Qty" type="number" size="small" fullWidth value={current.freeQty} onChange={handleChange('freeQty')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.PER) && (
                                <TextField label="Per" size="small" fullWidth value={current.per} onChange={handleChange('per')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {(isColVisible(COL.PO_NO) || isColVisible(COL.PO_DATE) || isColVisible(COL.PO_QTY)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.PO_NO) && (
                                <TextField label={getColLabel(COL.PO_NO, 'PO No')} size="small" fullWidth value={current.PONo} onChange={handleChange('PONo')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.PO_DATE) && (
                                <TextField label={getColLabel(COL.PO_DATE, 'PO Date')} type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={current.PODate} onChange={handleChange('PODate')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.PO_QTY) && (
                                <TextField label={getColLabel(COL.PO_QTY, 'PO Qty')} type="number" size="small" fullWidth value={current.poQty} onChange={handleChange('poQty')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {(isColVisible(COL.OTH1) || isColVisible(COL.OTH2) || isColVisible(COL.OTH3)) && (
                        <Box display="flex" flexDirection="row" flexWrap="wrap" gap={1.5}>
                            {isColVisible(COL.OTH1) && (
                                <TextField label={getColLabel(COL.OTH1, 'Oth 1')} size="small" fullWidth type="number" value={current[othDisplayField('oth1', COL.OTH1)] || ''} onChange={handleOthChange('oth1', COL.OTH1)} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH1) && !isOthRupeesMode(COL.OTH1) && (
                                <TextField label={`${getColLabel(COL.OTH1, 'Oth 1')} Amount`} size="small" fullWidth disabled value={current.oth1Amt || ''} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH2) && (
                                <TextField label={getColLabel(COL.OTH2, 'Oth 2')} size="small" fullWidth type="number" value={current[othDisplayField('oth2', COL.OTH2)] || ''} onChange={handleOthChange('oth2', COL.OTH2)} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH2) && !isOthRupeesMode(COL.OTH2) && (
                                <TextField label={`${getColLabel(COL.OTH2, 'Oth 2')} Amount`} size="small" fullWidth disabled value={current.oth2Amt || ''} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH3) && (
                                <TextField label={getColLabel(COL.OTH3, 'Oth 3')} size="small" fullWidth type="number" value={current[othDisplayField('oth3', COL.OTH3)] || ''} onChange={handleOthChange('oth3', COL.OTH3)} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH3) && !isOthRupeesMode(COL.OTH3) && (
                                <TextField label={`${getColLabel(COL.OTH3, 'Oth 3')} Amount`} size="small" fullWidth disabled value={current.oth3Amt || ''} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {isColVisible(COL.SERIAL_NO) && <TextField size="small" label={getColLabel(COL.SERIAL_NO, 'Serial No')} value={current.serialNo || ''} onChange={(e) => setCurrent(p => ({ ...p, serialNo: e.target.value }))} fullWidth />}
                    {(isColVisible(COL.EX4) || isColVisible(COL.EX5) || isColVisible(COL.EX_QTY6)) && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                            {isColVisible(COL.EX4) && <TextField size="small" label={getColLabel(COL.EX4, 'EX4')} value={current.ex4 || ''} onChange={(e) => setCurrent(p => ({ ...p, ex4: e.target.value }))} />}
                            {isColVisible(COL.EX5) && <TextField size="small" label={getColLabel(COL.EX5, 'EX5')} value={current.ex5 || ''} onChange={(e) => setCurrent(p => ({ ...p, ex5: e.target.value }))} />}
                            {isColVisible(COL.EX_QTY6) && <TextField size="small" label={getColLabel(COL.EX_QTY6, 'EXQty6')} type="number" value={current.exQty6 || ''} onChange={(e) => setCurrent(p => ({ ...p, exQty6: e.target.value }))} />}
                        </Box>
                    )}
                    {(isColVisible(COL.OTH4) || isColVisible(COL.OTH5)) && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))', gap: 2, mb: 2 }}>
                            {isColVisible(COL.OTH4) && <TextField size="small" type="number" label={getColLabel(COL.OTH4, 'Oth 4')} value={current[othDisplayField('oth4', COL.OTH4)] || ''} onChange={handleOthChange('oth4', COL.OTH4)} />}
                            {isColVisible(COL.OTH4) && !isOthRupeesMode(COL.OTH4) && <TextField size="small" disabled label={`${getColLabel(COL.OTH4, 'Oth 4')} Amount`} value={current.oth4Amt || ''} />}
                            {isColVisible(COL.OTH5) && <TextField size="small" type="number" label={getColLabel(COL.OTH5, 'Oth 5')} value={current[othDisplayField('oth5', COL.OTH5)] || ''} onChange={handleOthChange('oth5', COL.OTH5)} />}
                            {isColVisible(COL.OTH5) && !isOthRupeesMode(COL.OTH5) && <TextField size="small" disabled label={`${getColLabel(COL.OTH5, 'Oth 5')} Amount`} value={current.oth5Amt || ''} />}
                        </Box>
                    )}
                    {(isColVisible(COL.OTH6) || isColVisible(COL.OTH7)) && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))', gap: 2, mb: 2 }}>
                            {isColVisible(COL.OTH6) && <TextField size="small" type="number" label={getColLabel(COL.OTH6, 'Oth 6')} value={current[othDisplayField('oth6', COL.OTH6)] || ''} onChange={handleOthChange('oth6', COL.OTH6)} />}
                            {isColVisible(COL.OTH6) && !isOthRupeesMode(COL.OTH6) && <TextField size="small" disabled label={`${getColLabel(COL.OTH6, 'Oth 6')} Amount`} value={current.oth6Amt || ''} />}
                            {isColVisible(COL.OTH7) && <TextField size="small" type="number" label={getColLabel(COL.OTH7, 'Oth 7')} value={current[othDisplayField('oth7', COL.OTH7)] || ''} onChange={handleOthChange('oth7', COL.OTH7)} />}
                            {isColVisible(COL.OTH7) && !isOthRupeesMode(COL.OTH7) && <TextField size="small" disabled label={`${getColLabel(COL.OTH7, 'Oth 7')} Amount`} value={current.oth7Amt || ''} />}
                        </Box>
                    )}
                    {(isColVisible(COL.UTGST) || isColVisible(COL.GST_CESS) || isColVisible(COL.GST_OTH)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.UTGST) && (
                                <TextField label={getColLabel(COL.UTGST, 'UTGST')} size="small" fullWidth type="number" value={current.UTGST} onChange={handleChange('UTGST')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.GST_CESS) && (
                                <TextField label={getColLabel(COL.GST_CESS, 'GST Cess')} size="small" fullWidth type="number" value={current.GSTCess} onChange={handleChange('GSTCess')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.GST_OTH) && (
                                <TextField label={getColLabel(COL.GST_OTH, 'GST Oth')} size="small" fullWidth type="number" value={current.GSTOth} onChange={handleChange('GSTOth')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {isColVisible(COL.AMOUNT) && (
                        <TextField label={getColLabel(COL.AMOUNT, 'Amount')} fullWidth disabled type="number" size="small" value={current.amount} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                    )}
                    {isColVisible(COL.REMARK) && (
                        <Box>
                            <TextField label={getColLabel(COL.REMARK, 'Remark')} fullWidth multiline minRows={1} maxRows={3} size="small" value={current.remark || ''} onChange={handleChange('remark')} inputProps={{ maxLength: 250 }} helperText={`${(current.remark || '').length}/250 characters`} />
                        </Box>
                    )}
                    <Box display="flex" justifyContent="center">
                        <Button variant="outlined" onClick={addProduct} sx={{ minWidth: 180 }}>
                            {editIndex != null ? 'Update Product' : 'Add Product'}
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ width: isSmall ? '100%' : 670, maxWidth: '100%', p: isSmall ? 0 : 0, pl: 2, marginTop: isMobile ? -1 : -4, flexShrink: 0 }}>
                    {isMobile ? (
                        <Box>
                            {rows.filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300).map((row, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        border: "1px solid #cbd5e1",
                                        borderRadius: 2,
                                        p: 1.5,
                                        mb: 1.5,
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 600 }}>
                                        {row.productCode} â€”{" "}
                                        <Box
                                            component="span"
                                            sx={{
                                                fontStyle: "italic",
                                                fontWeight: "normal",
                                                display: "inline",
                                                wordBreak: "break-word",
                                                whiteSpace: "normal"
                                            }}
                                        >
                                            {row.description}
                                        </Box>
                                    </Typography>

                                    {(row.ChallanNumber || row.ChallanDate) && (
                                        <Typography variant="caption" display="block" color='blue'>
                                            {row.ChallanNumber && `InvoiceNo: ${row.ChallanNumber}`}
                                            {row.ChallanDate && ` | Date: ${row.ChallanDate}`}
                                        </Typography>
                                    )}

                                    <Grid container justifyContent="space-between">
                                        <Typography>Qty:</Typography>
                                        <Typography>{row.dispQty ?? row.qty}</Typography>
                                    </Grid>

                                    <Grid container justifyContent="space-between">
                                        <Typography>Rate:</Typography>
                                        <Typography>₹{row.rate}</Typography>
                                    </Grid>

                                    <Grid container justifyContent="space-between">
                                        <Typography>Disc:</Typography>
                                        <Typography>{row.disc}%</Typography>
                                    </Grid>

                                    <Grid container justifyContent="space-between">
                                        <Typography>Amount:</Typography>
                                        <Typography>₹{row.amount}</Typography>
                                    </Grid>

                                    {/* TAX INFO */}
                                    <Grid container justifyContent="space-between">
                                        <Typography>GST:</Typography>
                                        <Typography>{row.tax}%</Typography>
                                    </Grid>

                                    {state?.toLowerCase() === compState?.toLowerCase() ? (
                                        <>
                                            <Grid container justifyContent="space-between">
                                                <Typography>CGST:</Typography>
                                                <Typography>₹{formatINR(row.CGSTAmt)}</Typography>
                                            </Grid>
                                            <Grid container justifyContent="space-between">
                                                <Typography>SGST:</Typography>
                                                <Typography>₹{formatINR(row.SGSTAmt)}</Typography>
                                            </Grid>
                                        </>
                                    ) : (
                                        <Grid container justifyContent="space-between">
                                            <Typography>IGST:</Typography>
                                            <Typography>₹{formatINR(row.IGSTAmt)}</Typography>
                                        </Grid>
                                    )}

                                    {/* Remark */}
                                    {row.remark && row.remark.trim() !== '' && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                mt: 1,
                                                display: 'block',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                whiteSpace: 'normal'
                                            }}
                                        >
                                            Remark: {row.remark}
                                        </Typography>
                                    )}

                                    {/* DELETE / EDIT BUTTONS IF NEEDED */}
                                    <Box display="flex" justifyContent="flex-end" mt={1} gap={0.5}>
                                        <IconButton size="small" onClick={() => editRow(i)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => deleteRow(i)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <DataGrid
                            rows={rows.filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300).map((r, i) => ({ ...r, id: i + 1, index: i }))}
                            columns={columns}
                            getRowHeight={() => 'auto'}
                            autoHeight
                            disableRowSelectionOnClick
                            hideFooter
                            sx={{
                                mt: 5,
                                border: '1px solid #cbd5e1',
                                borderRadius: 1,
                                '& .MuiDataGrid-columnHeaders': { minHeight: 55, maxHeight: 55 },
                                '& .MuiDataGrid-columnHeader': { backgroundColor: '#f0f0f0', paddingTop: '4px', paddingBottom: '4px', height: 42, lineHeight: '1.2rem', fontSize: '13px' },
                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#edf2feff' },
                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#ffffff' }
                            }}
                        />
                    )}
                    <Box mt={3}>
                        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography>Basic Amount</Typography>
                            <Typography>₹{formatINR(totals.basic)}</Typography>
                        </Grid>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                            Total Discounted Amount: ₹{formatINR(totals.discount)}
                        </Typography>

                        {additionalCharges.length > 0 && (
                            <Box mb={2}>
                                <Accordion
                                    expanded={chargesCollapsibleOpen !== false ? true : chargesCollapsibleOpen}
                                    onChange={() => setChargesCollapsibleOpen(!chargesCollapsibleOpen)}
                                    sx={{ width: '100%' }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.75, minHeight: 'auto' }}>
                                        <Typography variant="subtitle2">Charges</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ py: 1, px: 1 }}>
                                        <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <colgroup>
                                                    <col style={{ width: '45%' }} />
                                                    <col style={{ width: '18%' }} />
                                                    <col style={{ width: '18%' }} />
                                                    <col style={{ width: '19%' }} />
                                                </colgroup>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderRight: '1px solid #ddd', fontWeight: 600, fontSize: '12px' }}>Charge Name</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderRight: '1px solid #ddd', fontWeight: 600, fontSize: '12px' }}>Amount</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderRight: '1px solid #ddd', fontWeight: 600, fontSize: '12px' }}>Tax</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, fontSize: '12px' }}>Final Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {additionalCharges.map(charge => {
                                                        const key = charge.TransType;
                                                        if (!chargeInputs[key]) {
                                                            updateChargeInput(key, { amount: '', taxId: charge.TaxId ?? null });
                                                        }
                                                        const input = chargeInputs[key] || { amount: '', taxId: charge.TaxId ?? null };
                                                        const { finalAmount } = getChargeFinalAmounts(charge, input);

                                                        return (
                                                            <tr key={key} style={{ borderBottom: '1px solid #ddd' }}>
                                                                <td style={{ padding: '6px 8px', borderRight: '1px solid #ddd', verticalAlign: 'middle' }}>
                                                                    <Typography variant="caption" sx={{ fontSize: '12px' }}>{charge.ChargeName}</Typography>
                                                                </td>
                                                                <td style={{ padding: '6px 8px', borderRight: '1px solid #ddd', verticalAlign: 'middle' }}>
                                                                    <TextField
                                                                        size="small"
                                                                        fullWidth
                                                                        type="number"
                                                                        value={input.amount}
                                                                        onChange={(e) => updateChargeInput(key, { amount: e.target.value })}
                                                                        sx={{ '& .MuiOutlinedInput-root': { height: 30, fontSize: '12px' } }}
                                                                    />
                                                                </td>
                                                                <td style={{ padding: '6px 8px', borderRight: '1px solid #ddd', verticalAlign: 'middle' }}>
                                                                    {charge.TaxEnabled ? (
                                                                        <Dropdown
                                                                            id={`charge-tax-${key}`}
                                                                            valueKey="Id"
                                                                            labelKey="value"
                                                                            value={input.taxId ? String(input.taxId) : ""}
                                                                            options={gsts}
                                                                            onChange={(opt) => updateChargeInput(key, { taxId: opt ? opt.Id : null })}
                                                                        />
                                                                    ) : (
                                                                        <Typography variant="caption" sx={{ color: '#999', fontSize: '12px' }}>N/A</Typography>
                                                                    )}
                                                                </td>
                                                                <td style={{ padding: '6px 8px', verticalAlign: 'middle' }}>
                                                                    <TextField
                                                                        size="small"
                                                                        fullWidth
                                                                        type="number"
                                                                        value={finalAmount.toFixed(2)}
                                                                        disabled
                                                                        sx={{ '& .MuiOutlinedInput-root': { height: 30, backgroundColor: '#f5f5f5', fontSize: '12px' } }}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        )}

                        {state?.toLowerCase() === compState?.toLowerCase() ? (
                            <>
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>CGST</Typography>
                                    <Typography>₹{formatINR(Number(totals.cgst) + chargesSummary.cgst)}</Typography>
                                </Grid>
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>SGST</Typography>
                                    <Typography>₹{formatINR(Number(totals.sgst) + chargesSummary.sgst)}</Typography>
                                </Grid>
                            </>
                        ) : (
                            totals.igst > 0 && (
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>IGST</Typography>
                                    <Typography>₹{formatINR(Number(totals.igst) + chargesSummary.igst)}</Typography>
                                </Grid>
                            )
                        )}
                        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography>Round Off ({Number(totals.roundOff) >= 0 ? '+' : '-'})</Typography>
                            <Typography>₹{formatINR(totals.roundOff)}</Typography>
                        </Grid>

                        <Divider sx={{ my: 1 }} />
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={600}>Total Amount</Typography>
                            <Typography fontWeight={600}>₹{formatINR(Number(totals.total))}</Typography>
                        </Grid>
                    </Box>
                </Box>
            </Box>
            {!!errors.global && (
                <Typography color="error" sx={{ mt: 2 }}>
                    {errors.global}
                </Typography>
            )}
            <Box mt={1} display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={isEditingMode || isProductEditLocked}   // ðŸ‘‰ NEW: block Next while editing
                >
                    Next
                </Button>

            </Box>


            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={stockWarningOpen}
                onClose={() => {
                    setStockWarningOpen(false);
                    if (stockConfirmResolve) stockConfirmResolve(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                    ⚠️ Stock Warning
                </DialogTitle>
                <DialogContent sx={{ py: 3 }}>
                    <Typography sx={{ color: '#d32f2f', fontSize: '16px', lineHeight: 1.6 }}>
                        {stockWarningData?.message}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setStockWarningOpen(false);
                            if (stockConfirmResolve) stockConfirmResolve(false);
                        }}
                        sx={{ color: '#666', borderColor: '#ddd' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setStockWarningOpen(false);
                            if (stockConfirmResolve) stockConfirmResolve(true);
                        }}
                        sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
                    >
                        Yes, Save Anyway
                    </Button>
                </DialogActions>
            </Dialog>

            {/* "Enable Convesrion effect in Transaction?" — Conversion Qty popup, shown when Unit is
                changed away from the product's default Unit. */}
            <Dialog
                open={conversionQtyDialogOpen}
                onClose={handleCancelConversionQty}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                    Unit Conversion
                </DialogTitle>
                <DialogContent sx={{ py: 3 }}>
                    <Typography sx={{ fontSize: '14px', color: '#555', mb: 2 }}>
                        How many {pendingUnitChange?.unit || 'unit'} make 1 {current.unit || 'base unit'}?
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Conversion Qty"
                        size="small"
                        type="text"
                        value={conversionQtyInput}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d*\.?\d{0,4}$/.test(val)) setConversionQtyInput(val);
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={handleCancelConversionQty}
                        sx={{ color: '#666', borderColor: '#ddd' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmConversionQty}
                        sx={{ bgcolor: '#1877F2', '&:hover': { bgcolor: '#0d5dc7' } }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

        </Paper>
    );
};

export default ProductDetails;
